import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Slide,
  Button,
  Chip,
  Autocomplete,
  Tooltip,
  Alert
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme, alpha } from '@mui/material/styles'; // Moved alpha here as it's used with theme
import { format } from 'date-fns';
import useSupabaseClient from '../hooks/useSupabaseClient';
import DraggableDialogPaper from './DraggableDialogPaper';
import InvoiceItemFormModal from './InvoiceItemFormModal';
import PurchaseOrderAutoFillModal from './PurchaseOrderAutoFillModal';
import { writeInvoiceItem, itemAmountsChanged } from '../lib/invoiceItemEditor.mjs';
import { isDemoAccount, formatColombianDate, formatColombianCurrency } from '../lib/demoAccount.mjs';
import ColombianDateField from './ColombianDateField';

const filterOptionsLimit = createFilterOptions({
  limit: 50,
  stringify: (option) => typeof option === 'string' ? option : `${option.codigo} ${option.descripcion}`
});

const sortInvoiceItemsById = (items) => {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const aId = Number(a?.id);
    const bId = Number(b?.id);
    if (!Number.isFinite(aId) && !Number.isFinite(bId)) return 0;
    if (!Number.isFinite(aId)) return 1;
    if (!Number.isFinite(bId)) return -1;
    return aId - bId;
  });
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  cursor: 'move',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const InfoSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const InfoGrid = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '0.875rem',
}));

const InfoValue = styled(Typography)(({ _theme }) => ({
  fontWeight: 400,
}));

const SummaryRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'total',
})(({ theme, total }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  backgroundColor: total ? theme.palette.primary.light : 'transparent',
  color: total ? 'white' : 'inherit',
  borderRadius: total ? theme.shape.borderRadius : 0,
  fontWeight: total ? 600 : 400,
}));

const WarehouseAutocomplete = ({ initialValue, onChange, userId, supabase }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!open || !userId || !supabase) return;
    
    setLoading(true);
    const doSearch = async () => {
      try {
        const t = search.trim();
        let query = supabase.from('invoice_erp_locations').select('codigo, descripcion').eq('tenant_id', userId).limit(50);
        if (t) {
          query = query.or(`codigo.ilike.%${t}%,descripcion.ilike.%${t}%`);
        }
        const { data, error } = await query;
        if (!error && data) {
          setOptions(data);
        }
      } catch (err) {
        console.error('Error searching locations:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const timeoutId = setTimeout(doSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [search, open, userId, supabase]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
      getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} - ${option.descripcion}`}
      options={options}
      loading={loading}
      inputValue={search}
      onInputChange={(event, newInputValue, reason) => {
        setSearch(newInputValue);
        if (reason === 'clear') {
          onChange(null);
        }
      }}
      value={options.find(o => o.codigo === initialValue) || (initialValue ? { codigo: initialValue, descripcion: '' } : null)}
      onChange={(event, newValue) => {
        const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : null;
        onChange(newCode);
        if (newValue && typeof newValue !== 'string') {
          setSearch(`${newValue.codigo} - ${newValue.descripcion}`);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Buscar bodega..."
          sx={{ width: '150px' }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

const FreightProviderAutocomplete = ({ initialValue, onChange, userId, supabase, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!open || !userId || !supabase) return;
    
    setLoading(true);
    const doSearch = async () => {
      try {
        const t = search.trim();
        let query = supabase.from('invoice_third_parties').select('nit, company_short, company').eq('user_id', userId).limit(50);
        if (t) {
          query = query.or(`nit.ilike.%${t}%,company_short.ilike.%${t}%,company.ilike.%${t}%`);
        }
        const { data, error } = await query;
        if (!error && data) {
          setOptions(data);
        }
      } catch (err) {
        console.error('Error searching third parties:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const timeoutId = setTimeout(doSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [search, open, userId, supabase]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => option.nit === value.nit}
      getOptionLabel={(option) => typeof option === 'string' ? option : `${option.nit} - ${option.company_short || option.company}`}
      options={options}
      loading={loading}
      inputValue={search}
      onInputChange={(event, newInputValue, reason) => {
        setSearch(newInputValue);
        if (reason === 'clear') {
          onChange(null);
        }
      }}
      value={options.find(o => o.nit === initialValue) || (initialValue ? { nit: initialValue, company_short: initialValue } : null)}
      onChange={(event, newValue) => {
        const newNit = newValue ? (typeof newValue === 'string' ? newValue : newValue.nit) : null;
        onChange(newNit);
        if (newValue && typeof newValue !== 'string') {
          setSearch(`${newValue.nit} - ${newValue.company_short || newValue.company}`);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Tercero Flete (Opcional)"
          placeholder="Buscar nit o nombre..."
          fullWidth
          margin="dense"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

const InvoiceDetailModal = ({ invoice, open, onClose, invoiceStore }) => {
  const theme = useTheme();
  const { supabase } = useSupabaseClient();
  const isServiceInvoice = String(invoice?.invoice_type || '').toLowerCase() === 'servicio';
  const isInventoryInvoice = String(invoice?.invoice_type || '').toLowerCase() === 'inventario';
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [editedDate, setEditedDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState(() => sortInvoiceItemsById(invoice.invoice_items));
  const [productById, setProductById] = useState({});
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  
  const [prorationModalOpen, setProrationModalOpen] = useState(false);
  const [prorationAmount, setProrationAmount] = useState('');
  const [freightProviderNit, setFreightProviderNit] = useState(invoice?.freight_provider_nit || '');
  const [isProrating, setIsProrating] = useState(false);
  const [displayedInvoiceTotals, setDisplayedInvoiceTotals] = useState(() => ({
    subtotal: Number(invoice?.subtotal) || 0,
    tax: Number(invoice?.tax) || 0,
    discount: Number(invoice?.discount) || 0,
    total: Number(invoice?.total) || 0
  }));

  const [poAutoFillModalOpen, setPoAutoFillModalOpen] = useState(false);
  const [currentPurchaseOrderMapping, setCurrentPurchaseOrderMapping] = useState(() => (
    Array.isArray(invoice?.purchase_order_mapping) ? invoice.purchase_order_mapping : []
  ));

  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemMessage, setItemMessage] = useState('');
  const savedItemForRetry = useRef(null);
  const itemSaveBusy = useRef(false);

  const openItemEditor = (item = null) => {
    if (item && userConfig?.enable_invoice_item_editing !== true) return;
    savedItemForRetry.current = null;
    setEditingItem(item);
    setItemMessage('');
    setItemFormOpen(true);
  };

  const { userId, exactCodeEnabled, enableInventoryProration, enablePurchaseOrders, poPriorityMode } = useMemo(() => {
    const storedUser = localStorage.getItem('invoice_user');
    if (!storedUser) return { userId: null, exactCodeEnabled: false, enableInventoryProration: false };
    try {
      const user = JSON.parse(storedUser);
      return {
        userId: user?.id || null,
        exactCodeEnabled: user?.exact_provider_item_code === true,
        enableInventoryProration: user?.enable_inventory_proration === true,
        enablePurchaseOrders: user?.enable_purchase_orders === true,
        poPriorityMode: user?.po_priority_mode === true
      };
    } catch {
      return { userId: null, exactCodeEnabled: false, enableInventoryProration: false, enablePurchaseOrders: false, poPriorityMode: false };
    }
  }, []);

  const authState = { user: { id: userId } }; // mocked simple authState for the InvoiceItemFormModal

  if (!invoice) return null;

  const { updateInvoiceStatus } = invoiceStore.getState();

  const [userConfig, setUserConfig] = useState(null);

  useEffect(() => {
    setUserConfig(null);
    let active = true;
    if (open && userId && supabase) {
      const fetchUserConfig = async () => {
        try {
          const { data } = await supabase
            .from('invoice_users')
            .select('exact_provider_item_code, po_priority_mode, enable_invoice_item_editing')
            .eq('id', userId)
            .single();
          if (active && data) setUserConfig(data);
        } catch (err) {
          console.error("Error fetching user config in modal:", err);
        }
      };
      fetchUserConfig();
    }
    return () => { active = false; };
  }, [open, userId, supabase]);

  // Initialize edited date when invoice changes
  React.useEffect(() => {
    if (invoice?.date) {
      setEditedDate(new Date(invoice.date).toISOString().split('T')[0]);
    }
  }, [invoice?.date]);

  useEffect(() => {
    setInvoiceItems(sortInvoiceItemsById(invoice?.invoice_items));
  }, [invoice?.invoice_items]);

  useEffect(() => {
    setCurrentPurchaseOrderMapping(
      Array.isArray(invoice?.purchase_order_mapping) ? invoice.purchase_order_mapping : []
    );
  }, [invoice?.id, invoice?.purchase_order_mapping]);

  useEffect(() => {
    setDisplayedInvoiceTotals({
      subtotal: Number(invoice?.subtotal) || 0,
      tax: Number(invoice?.tax) || 0,
      discount: Number(invoice?.discount) || 0,
      total: Number(invoice?.total) || 0
    });
  }, [invoice?.id, invoice?.subtotal, invoice?.tax, invoice?.discount, invoice?.total]);

  const itemProductIds = useMemo(() => {
    const ids = (invoiceItems || [])
      .map((i) => i?.product_id)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
    return Array.from(new Set(ids.map((v) => String(v))));
  }, [invoiceItems]);

  useEffect(() => {
    const loadProductsForItems = async () => {
      if (!supabase) return;
      if (!userId) return;
      if (!open) return;
      if (!itemProductIds.length) {
        setProductById({});
        return;
      }

      const { data, error } = await supabase
        .from('invoice_products')
        .select('id,item_code,description')
        .eq('user_id', userId)
        .in('id', itemProductIds);

      if (error) {
        console.error('Error loading invoice_products for invoice items:', error);
        return;
      }

      const next = {};
      for (const p of data || []) {
        next[String(p.id)] = p;
      }
      setProductById(next);
    };

    loadProductsForItems();
  }, [supabase, userId, open, itemProductIds]);

  useEffect(() => {
    const doSearch = async () => {
      if (!supabase) return;
      if (!userId) return;
      if (!productPickerOpen) return;

      const term = productSearch.trim();
      if (!term) {
        setProductOptions([]);
        return;
      }

      setIsSearchingProducts(true);

      // ====== MODO PRIORIDAD OC: Buscar en ítems de OC del proveedor ======
      if (poPriorityMode && invoice?.num_identificacion) {
        const { data: poData, error: poError } = await supabase
          .rpc('find_similar_po_details', {
            p_description: term,
            p_user_id: userId,
            p_supplier_nit: invoice.num_identificacion,
            p_limit: 25,
            p_similarity_threshold: 0.2,
          });

        setIsSearchingProducts(false);
        if (!poError && Array.isArray(poData) && poData.length > 0) {
          const assignedQtyByDetail = {};
          currentPurchaseOrderMapping
            .filter(mapping => String(mapping.invoice_item_id) !== String(activeItem?.id))
            .forEach(mapping => {
              const key = `${mapping.po_id}:${mapping.po_conteo}`;
              assignedQtyByDetail[key] = (assignedQtyByDetail[key] || 0)
                + (parseFloat(mapping.assigned_qty) || 0);
            });

          const mapped = poData
            .map((r) => {
              const key = `${r.order_id}:${r.conteo}`;
              const availableQty = Math.max(
                0,
                (parseFloat(r.faltante) || 0) - (assignedQtyByDetail[key] || 0)
              );
              return {
                id: r.detail_id,
                item_code: r.item,
                description: r.description,
                similarity_score: r.similarity_score,
                // Datos extra de la OC para override de valores
                _isPODetail: true,
                _poTipo: r.po_tipo,
                _poNumber: r.po_number,
                _poFecha: r.po_fecha,
                _poConteo: r.conteo,
                _poQty: Math.min(parseFloat(r.qty) || availableQty, availableQty),
                _poFaltante: availableQty,
                _poRawFaltante: parseFloat(r.faltante) || 0,
                _poCost: r.cost,
                _poVatRate: parseFloat(r.porc_iva) || 0,
                _poOrderId: r.order_id,
              };
            })
            .filter(option => option._poFaltante > 0);
          setProductOptions(mapped);
        } else {
          setProductOptions([]);
        }
        return;
      }

      if (exactCodeEnabled) {
        // Exact match search by item_code
        const { data: exactData, error: exactError } = await supabase
          .from('invoice_products')
          .select('id, item_code, description')
          .eq('user_id', userId)
          .eq('item_code', term);
          
        if (!exactError && Array.isArray(exactData) && exactData.length > 0) {
          const mappedExact = exactData.map(r => ({
            id: r.id,
            item_code: r.item_code,
            description: r.description,
            similarity_score: 1.0 // Artificial exact match score
          }));
          setIsSearchingProducts(false);
          setProductOptions(mappedExact);
          return;
        }
      }

      // Prefer pg_trgm similarity search via RPC
      const { data: simData, error: simError } = await supabase
        .rpc('find_similar_products', {
          p_description: term,
          p_user_id: userId,
          p_limit: 25,
          p_similarity_threshold: 0.3,
        });

      if (!simError && Array.isArray(simData) && simData.length > 0) {
        const mapped = simData.map((r) => ({
          id: r.product_id,
          item_code: r.item_code,
          description: r.description,
          similarity_score: r.similarity_score,
        }));
        setIsSearchingProducts(false);
        setProductOptions(mapped);
        return;
      }

      // Fallback to ilike search via RPC (strictly filtered by user_id and sync_status)
      if (simError) {
        console.warn('RPC find_similar_products failed, falling back to ilike search RPC:', simError);
      }

      const { data, error } = await supabase.rpc('search_products_ilike', {
        p_term: term,
        p_user_id: userId,
        p_limit: 25
      });

      setIsSearchingProducts(false);
      if (error) {
        console.error('Error searching invoice_products:', error);
        setProductOptions([]);
        return;
      }
      setProductOptions(data || []);
    };

    const t = setTimeout(doSearch, 250);
    return () => clearTimeout(t);
  }, [supabase, userId, productPickerOpen, productSearch, poPriorityMode, invoice?.num_identificacion, currentPurchaseOrderMapping, activeItem?.id]);

  const openProductPicker = (item) => {
    setActiveItem(item);
    const current = item?.product_id ? productById[String(item.product_id)] : null;
    setSelectedProduct(current || null);
    setProductSearch('');
    setProductOptions([]);
    setProductPickerOpen(true);
  };

  const closeProductPicker = () => {
    setProductPickerOpen(false);
    setActiveItem(null);
    setProductSearch('');
    setProductOptions([]);
    setSelectedProduct(null);
  };

  const handleSaveProductId = async () => {
    if (!activeItem?.id) return;
    if (!userId) {
      alert('Usuario no autenticado');
      return;
    }

    setIsSavingProduct(true);
    try {
      // ====== MODO PRIORIDAD OC: override de valores desde la OC ======
      if (poPriorityMode && selectedProduct?._isPODetail) {
        const selectedDetailKey = `${selectedProduct._poOrderId}:${selectedProduct._poConteo}`;
        const alreadyAssignedQty = currentPurchaseOrderMapping
          .filter(mapping => String(mapping.invoice_item_id) !== String(activeItem.id))
          .filter(mapping => `${mapping.po_id}:${mapping.po_conteo}` === selectedDetailKey)
          .reduce((total, mapping) => total + (parseFloat(mapping.assigned_qty) || 0), 0);
        const availableQty = Math.max(
          0,
          (parseFloat(selectedProduct._poRawFaltante) || 0) - alreadyAssignedQty
        );

        if (availableQty <= 0) {
          alert('Este renglón de la orden de compra ya fue asignado completamente a otro ítem.');
          return;
        }

        // 1. Buscar el product_id real en invoice_products por item_code
        let resolvedProductId = null;
        if (selectedProduct.item_code) {
          const { data: prodMatch } = await supabase
            .from('invoice_products')
            .select('id')
            .eq('user_id', userId)
            .eq('item_code', selectedProduct.item_code)
            .limit(1);
          if (prodMatch && prodMatch.length > 0) {
            resolvedProductId = prodMatch[0].id;
          }
        }

        // 2. Actualizar invoice_items con valores de la OC
        const poQty = Math.min(
          parseFloat(selectedProduct._poQty) || parseFloat(activeItem.quantity) || 0,
          availableQty
        );
        const { data: selectedPoDetail, error: selectedPoDetailError } = await supabase
          .from('pending_purchase_order_details')
          .select('porc_iva')
          .eq('id', selectedProduct.id)
          .single();
        if (selectedPoDetailError) throw selectedPoDetailError;

        const poCost = parseFloat(selectedProduct._poCost) || activeItem.unit_price;
        const poTotalPrice = poQty * poCost;
        const poVatRate = parseFloat(selectedPoDetail?.porc_iva) || 0;
        const poTax = poTotalPrice * (poVatRate / 100);

        const { error: updateError } = await supabase
          .from('invoice_items')
          .update({
            product_id: resolvedProductId,
            product_match_manual: true,
            quantity: poQty,
            unit_price: poCost,
            original_unit_price: null,
            total_price: poTotalPrice,
            tax: poTax,
          })
          .eq('id', activeItem.id)
          .eq('user_id', userId);

        if (updateError) throw updateError;

        // 3. Actualizar purchase_order_mapping en la factura
        const existingMapping = currentPurchaseOrderMapping;
        const newMappingEntry = {
          invoice_item_id: activeItem.id,
          invoice_item_description: activeItem.description,
          product_code: selectedProduct.item_code,
          po_id: selectedProduct._poOrderId,
          po_tipo: selectedProduct._poTipo,
          po_number: selectedProduct._poNumber,
          po_conteo: selectedProduct._poConteo,
          assigned_qty: poQty,
          original_qty_in_invoice: parseFloat(activeItem.quantity) || 0,
          original_line_subtotal: poTotalPrice,
          original_tax_in_invoice: poTax,
          po_vat_rate: poVatRate,
        };
        const filteredMapping = existingMapping.filter(
          mapping => String(mapping.invoice_item_id) !== String(activeItem.id)
        );
        filteredMapping.push(newMappingEntry);

        const { error: mappingUpdateError } = await supabase
          .from('invoices')
          .update({
            purchase_order_mapping: filteredMapping,
            proration_amount: null,
            freight_provider_nit: null
          })
          .eq('id', invoice.id);
        if (mappingUpdateError) throw mappingUpdateError;

        setCurrentPurchaseOrderMapping(filteredMapping);

        if (invoiceStore?.getState()?.updateInvoice) {
          await invoiceStore.getState().updateInvoice(invoice.id);
        }

        const updatedItems = (invoiceItems || []).map((it) =>
            it.id === activeItem.id
              ? { ...it, product_id: resolvedProductId, product_match_manual: true, quantity: poQty, unit_price: poCost, original_unit_price: null, total_price: poTotalPrice, tax: poTax }
              : it
        );
        setInvoiceItems(updatedItems);
        await recalculateInvoiceTotals(updatedItems);

        if (resolvedProductId) {
          setProductById((prev) => ({ ...prev, [String(resolvedProductId)]: { id: resolvedProductId, item_code: selectedProduct.item_code, description: selectedProduct.description } }));
        }

        closeProductPicker();
        return;
      }

      // ====== MODO NORMAL: solo actualizar product_id ======
      const nextProductId = selectedProduct?.id || null;
      const { error } = await supabase
        .from('invoice_items')
        .update({ product_id: nextProductId, product_match_manual: true })
        .eq('id', activeItem.id)
        .eq('user_id', userId);

      if (error) throw error;

      if (invoiceStore?.getState()?.updateInvoice) {
        await invoiceStore.getState().updateInvoice(invoice.id);
      }

      setInvoiceItems((prev) =>
        (prev || []).map((it) =>
          it.id === activeItem.id ? { ...it, product_id: nextProductId, product_match_manual: true } : it
        )
      );

      if (selectedProduct?.id) {
        setProductById((prev) => ({ ...prev, [String(selectedProduct.id)]: selectedProduct }));
      }

      closeProductPicker();
    } catch (error) {
      console.error('Error updating invoice_items.product_id:', error);
      alert('Error al actualizar el producto del item');
    } finally {
      setIsSavingProduct(false);
    }
  };

  const handleDateEdit = () => {
    setIsEditingDate(true);
  };

  const handleDateSave = async () => {
    if (!editedDate || !invoice?.id) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('invoices')
        .update({ date: editedDate })
        .eq('id', invoice.id);

      if (error) throw error;

      // Update the invoice in the store if available
      if (invoiceStore?.getState()?.updateInvoice) {
        invoiceStore.getState().updateInvoice(invoice.id, { date: editedDate });
      }

      setIsEditingDate(false);
    } catch (error) {
      console.error('Error updating invoice date:', error);
      alert('Error al actualizar la fecha de la factura');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDateCancel = () => {
    setEditedDate(new Date(invoice.date).toISOString().split('T')[0]);
    setIsEditingDate(false);
  };

  const handleUpdateWarehouse = (itemId, value) => {
    setInvoiceItems((prev) =>
      prev.map((it) => (it.id === itemId ? { ...it, warehouse_code: value } : it))
    );
  };

  const handleBlurWarehouse = async (itemId, newCode) => {
    if (!userId) return;
    try {
      const { error } = await supabase
        .from('invoice_items')
        .update({ warehouse_code: newCode || null })
        .eq('id', itemId)
        .eq('user_id', userId);

      if (error) throw error;
      
      if (invoiceStore?.getState()?.updateInvoice) {
        await invoiceStore.getState().updateInvoice(invoice.id);
      }
    } catch (error) {
      console.error('Error updating warehouse_code:', error);
      alert('Error al guardar la bodega');
    }
  };

  const formatCurrency = (amount) => {
    if (isDemoAccount(invoice?.user_id)) return formatColombianCurrency(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0); // Ensure amount || 0 for safety
  };

  const formatDate = (dateString) => {
    if (isDemoAccount(invoice?.user_id)) return formatColombianDate(dateString);
    if (!dateString) return 'Sin fecha';
    // Split the date string and create date with explicit year, month, day
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return format(date, 'MMMM d, yyyy');
  };
  
  const handleApprove = async () => {
    await updateInvoiceStatus(invoice.id, 'APROBADO');
    onClose();
  };
  
  const handleReject = async () => {
    await updateInvoiceStatus(invoice.id, 'DESAPROBADO');
    onClose();
  };

  const handleSaveItem = async (payload) => {
    if (itemSaveBusy.current) return;
    itemSaveBusy.current = true;
    try {
      const retryTotals = savedItemForRetry.current != null;
      const original = savedItemForRetry.current || editingItem;
      const saved = await writeInvoiceItem({supabase,userId,invoiceId:invoice.id,original,payload,requireEditingEnabled: Boolean(editingItem)});
      // If updating the invoice totals fails, retry this same row; never insert
      // another line or overwrite a concurrent edit using an obsolete snapshot.
      savedItemForRetry.current = saved;
      const newItems = sortInvoiceItemsById(invoiceItems.some(item => String(item.id) === String(saved.id))
        ? invoiceItems.map(item => String(item.id) === String(saved.id) ? {...item,...saved} : item)
        : [...invoiceItems,saved]);
      setInvoiceItems(newItems);
      const recalculate = retryTotals || itemAmountsChanged(original,saved);
      try {
        if (recalculate) await recalculateInvoiceTotals(newItems);
        else if (invoiceStore?.getState()?.updateInvoice) {
          await Promise.resolve(invoiceStore.getState().updateInvoice(invoice.id))
            .catch(error => console.warn('La línea quedó guardada; no se pudo refrescar la factura.',error));
        }
      } catch {
        const error = new Error('La línea quedó guardada, pero no se pudieron actualizar los totales. Pulsa Guardar otra vez para reintentarlo sin duplicar la línea.');
        error.lineSaved = true;
        throw error;
      }
      setItemFormOpen(false);
      setItemMessage(editingItem ? recalculate ? 'Línea actualizada y totales recalculados.' : 'Línea actualizada.' : 'Línea añadida y totales recalculados.');
      savedItemForRetry.current = null;
    } finally {
      itemSaveBusy.current = false;
    }
  };

  const [itemToDelete, setItemToDelete] = useState(null);

  const confirmDelete = (itemId) => {
    setItemToDelete(itemId);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    const itemId = itemToDelete;
    setItemToDelete(null);

    if (!userId || !invoice?.id) return;
    try {
      const { error } = await supabase
        .from('invoice_items')
        .delete()
        .eq('id', itemId)
        .eq('user_id', userId);

      if (error) throw error;
      
      const newItems = invoiceItems.filter(it => it.id !== itemId);
      setInvoiceItems(newItems);
      await recalculateInvoiceTotals(newItems);
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Error al eliminar la línea');
    }
  };

  const recalculateInvoiceTotals = async (currentItems) => {
    if (!invoice?.id) return;
    const newSubtotal = currentItems.reduce((acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return acc + (qty * price);
    }, 0);
    const newTax = currentItems.reduce((acc, item) => acc + (parseFloat(item.tax) || 0), 0);
    const newInc = currentItems.reduce((acc, item) => acc + (parseFloat(item.impuesto_consumo) || 0), 0);
    const newDiscount = Number(invoice.discount) || 0;
    const newTotal = newSubtotal + newTax + newInc - newDiscount;
    const previousTotals = displayedInvoiceTotals;
    const nextTotals = {
      subtotal: newSubtotal,
      tax: newTax,
      discount: newDiscount,
      total: newTotal
    };

    setDisplayedInvoiceTotals(nextTotals);
    
    try {
      const { error } = await supabase
        .from('invoices')
        .update({
          subtotal: newSubtotal,
          tax: newTax,
          total: newTotal
        })
        .eq('id', invoice.id)
        .eq('user_id', userId);
        
      if (error) throw error;
      
      if (invoiceStore?.getState()?.updateInvoice) {
        await Promise.resolve(invoiceStore.getState().updateInvoice(invoice.id)).catch(error => console.warn('No se pudo refrescar la factura; los totales sí quedaron guardados.', error));
      }
    } catch (err) {
      setDisplayedInvoiceTotals(previousTotals);
      console.error('Error recalculating totals:', err);
      throw err;
    }

    return nextTotals;
  };

  const isInvoiceBalanced = (invoice) => {
    if (!invoice?.accounting_entries || invoice.accounting_entries.length === 0) return true;
    const totalDebit = invoice.accounting_entries.reduce((acc, e) => acc + (Number(e.debit) || 0), 0);
    const totalCredit = invoice.accounting_entries.reduce((acc, e) => acc + (Number(e.credit) || 0), 0);
    return Math.abs(totalDebit - totalCredit) < 0.01;
  };

  const hasProratedItems = useMemo(() => {
    return invoiceItems.some(item => item.original_unit_price !== null && item.original_unit_price !== undefined);
  }, [invoiceItems]);

  const handleApplyProration = async () => {
    const amount = parseFloat(prorationAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Ingrese un valor válido a prorratear');
      return;
    }
    
    const purchaseOrderMapping = currentPurchaseOrderMapping;

    // La OC puede expresar la cantidad en unidades mientras la factura original
    // venía en cajas. El prorrateo debe conservar el valor económico de la línea,
    // no multiplicar el precio por caja por la nueva cantidad en unidades.
    const prorationBases = new Map();
    let mappingWithOriginalValues = purchaseOrderMapping.map((mapping) => {
      const item = invoiceItems.find(currentItem => String(currentItem.id) === String(mapping.invoice_item_id));
      if (!item) return mapping;

      const currentQty = parseFloat(item.quantity) || 0;
      const originalQty = parseFloat(mapping.original_qty_in_invoice) || currentQty;
      const storedOriginalPrice = parseFloat(item.original_unit_price ?? item.unit_price) || 0;
      const savedOriginalSubtotal = parseFloat(mapping.original_line_subtotal);
      const originalLineSubtotal = Number.isFinite(savedOriginalSubtotal)
        ? savedOriginalSubtotal
        : originalQty * storedOriginalPrice;

      prorationBases.set(item.id, originalLineSubtotal);
      return {
        ...mapping,
        original_line_subtotal: originalLineSubtotal,
        original_tax_in_invoice: Number.isFinite(parseFloat(mapping.original_tax_in_invoice))
          ? parseFloat(mapping.original_tax_in_invoice)
          : (parseFloat(item.tax) || 0)
      };
    });

    const totalOriginalValue = invoiceItems.reduce((acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const fallbackPrice = parseFloat(item.original_unit_price ?? item.unit_price) || 0;
      return acc + (prorationBases.get(item.id) ?? (fallbackPrice * qty));
    }, 0);

    if (totalOriginalValue === 0) {
      alert('No se puede prorratear si el valor total de los ítems es cero.');
      return;
    }

    setIsProrating(true);
    try {
      if (poPriorityMode && mappingWithOriginalValues.length > 0) {
        const poNumbers = [...new Set(mappingWithOriginalValues.map(mapping => String(mapping.po_number || '').trim()).filter(Boolean))];
        const { data: poDetails, error: poDetailsError } = poNumbers.length > 0
          ? await supabase
              .from('pending_purchase_order_details')
              .select('number, conteo, item, porc_iva')
              .eq('tenant_id', userId)
              .in('number', poNumbers)
          : { data: [], error: null };

        if (poDetailsError) throw poDetailsError;

        const vatRateByDetail = {};
        (poDetails || []).forEach(detail => {
          vatRateByDetail[`${detail.number}:${detail.conteo}:${detail.item}`] = parseFloat(detail.porc_iva) || 0;
          vatRateByDetail[`${detail.number}:${detail.conteo}`] = parseFloat(detail.porc_iva) || 0;
        });
        mappingWithOriginalValues = mappingWithOriginalValues.map(mapping => {
          const exactKey = `${mapping.po_number}:${mapping.po_conteo}:${mapping.product_code}`;
          const rowKey = `${mapping.po_number}:${mapping.po_conteo}`;
          const storedVatRate = parseFloat(mapping.po_vat_rate);
          const poVatRate = vatRateByDetail[exactKey]
            ?? vatRateByDetail[rowKey]
            ?? (Number.isFinite(storedVatRate) ? storedVatRate : null);
          if (poVatRate === null) {
            throw new Error(`No se encontró el IVA del renglón ${mapping.po_conteo} de la OC ${mapping.po_number}.`);
          }
          return { ...mapping, po_vat_rate: poVatRate };
        });
      }

      const mappingByItemId = {};
      mappingWithOriginalValues.forEach(mapping => {
        mappingByItemId[String(mapping.invoice_item_id)] = mapping;
      });

      const updatedItems = invoiceItems.map(item => {
        const qty = parseFloat(item.quantity) || 0;
        const fallbackPrice = parseFloat(item.original_unit_price ?? item.unit_price) || 0;
        const itemOriginalTotal = prorationBases.get(item.id) ?? (fallbackPrice * qty);
        const normalizedOriginalPrice = qty > 0
          ? itemOriginalTotal / qty
          : fallbackPrice;
        
        let newUnitPrice = normalizedOriginalPrice;
        if (qty > 0) {
          const itemProrationShare = (amount * (itemOriginalTotal / totalOriginalValue)) / qty;
          newUnitPrice += itemProrationShare;
        }

        const itemMapping = mappingByItemId[String(item.id)];
        const poVatRate = parseFloat(itemMapping?.po_vat_rate) || 0;
        const nextTax = poPriorityMode && itemMapping
          ? (newUnitPrice * qty * (poVatRate / 100))
          : (parseFloat(item.tax) || 0);

        return {
          ...item,
          original_unit_price: normalizedOriginalPrice,
          unit_price: newUnitPrice,
          total_price: newUnitPrice * qty,
          tax: nextTax
        };
      });

      for (const item of updatedItems) {
        const { error } = await supabase
          .from('invoice_items')
          .update({
            original_unit_price: item.original_unit_price,
            unit_price: item.unit_price,
            total_price: item.total_price,
            tax: item.tax
          })
          .eq('id', item.id)
          .eq('user_id', userId);
        if (error) throw error;
      }

      setInvoiceItems(updatedItems);
      setCurrentPurchaseOrderMapping(mappingWithOriginalValues);
      await recalculateInvoiceTotals(updatedItems);
      
      // Guardar el valor prorrateado y el nit del flete en la factura
      const { error: invoiceProrationError } = await supabase
        .from('invoices')
        .update({ 
          proration_amount: amount,
          freight_provider_nit: freightProviderNit || null,
          purchase_order_mapping: mappingWithOriginalValues.length > 0
            ? mappingWithOriginalValues
            : invoice.purchase_order_mapping
        })
        .eq('id', invoice.id)
        .eq('user_id', userId);
      if (invoiceProrationError) throw invoiceProrationError;

      setProrationModalOpen(false);
      setProrationAmount('');
      // No reseteamos el freightProviderNit para que quede preseleccionado
    } catch (err) {
      console.error('Error applying proration:', err);
      alert('Error al aplicar el prorrateo');
    } finally {
      setIsProrating(false);
    }
  };

  const handleRevertProration = async () => {
    if (!window.confirm('¿Está seguro de restaurar los precios originales?')) return;
    
    setIsProrating(true);
    try {
      const updatedItems = invoiceItems.map(item => {
        if (item.original_unit_price !== null && item.original_unit_price !== undefined) {
          return {
            ...item,
            unit_price: item.original_unit_price,
            total_price: item.original_unit_price * (parseFloat(item.quantity) || 0),
            tax: poPriorityMode
              ? (parseFloat(currentPurchaseOrderMapping.find(mapping => String(mapping.invoice_item_id) === String(item.id))?.original_tax_in_invoice) || 0)
              : item.tax,
            original_unit_price: null
          };
        }
        return item;
      });

      for (const item of updatedItems) {
        if (item.original_unit_price === null) {
          const { error } = await supabase
            .from('invoice_items')
            .update({
              unit_price: item.unit_price,
              total_price: item.total_price,
              tax: item.tax,
              original_unit_price: null
            })
            .eq('id', item.id)
            .eq('user_id', userId);
          if (error) throw error;
        }
      }

      setInvoiceItems(updatedItems);
      await recalculateInvoiceTotals(updatedItems);
      
      // Limpiar el valor prorrateado de la factura
      await supabase
        .from('invoices')
        .update({ proration_amount: null, freight_provider_nit: null })
        .eq('id', invoice.id)
        .eq('user_id', userId);

    } catch (err) {
      console.error('Error reverting proration:', err);
      alert('Error al restaurar precios originales');
    } finally {
      setIsProrating(false);
    }
  };

  const totalINC = useMemo(() => invoiceItems.reduce((acc, item) => acc + (Number(item.impuesto_consumo) || 0), 0), [invoiceItems]);
  const displayTax = displayedInvoiceTotals.tax;

  return (
    <>
      <InvoiceItemFormModal
        open={itemFormOpen}
        onClose={() => setItemFormOpen(false)}
        onSave={handleSaveItem}
        supabase={supabase}
        authState={authState}
        item={editingItem}
        product={editingItem?.product_id ? productById[String(editingItem.product_id)] : null}
      />
      
      {/* Confirm Delete Dialog */}
      <Dialog open={!!itemToDelete} onClose={() => setItemToDelete(null)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar esta línea? Esta acción no se puede deshacer y los totales se recalcularán automáticamente.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemToDelete(null)} color="primary">Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperComponent={DraggableDialogPaper}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 32px)',
            maxWidth: 1600,
            height: 'calc(100vh - 96px)',
          },
        }}
        TransitionComponent={Transition}
      >
        <StyledDialogTitle className="draggable-dialog-title">
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Detalles de la Factura
            </Typography>
            {invoice.estado && (
              <Chip 
                label={invoice.estado} 
                color={
                  invoice.estado === 'APROBADO' || invoice.estado === 'SINCRONIZADO' ? 'success' :
                  invoice.estado === 'DESAPROBADO' || invoice.estado === 'ERROR' ? 'error' :
                  invoice.estado === 'PENDIENTE' ? 'warning' :
                  'default'
                }
                size="small"
                sx={{ ml: 1.5, height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
              />
            )}
            {invoice.purchase_order_mapping && invoice.purchase_order_mapping.length > 0 && (
              <Chip
                label="🔗 Asociada a OC"
                color="info"
                size="small"
                sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, py: 0 }, fontWeight: 'bold' }}
              />
            )}
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            size="small"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 1, height: '100%', overflow: 'auto' }}>
          <InfoSection>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionTitle variant="h6">Información de la Factura</SectionTitle>
              <InfoGrid container>
                <Grid item xs={4}>
                  <InfoLabel>Número de Factura</InfoLabel>
                </Grid>
                <Grid item xs={8}>
                  <InfoValue variant="body1">{invoice.invoice_number}</InfoValue>
                </Grid>
              </InfoGrid>
              <InfoGrid container>
                <Grid item xs={4}>
                  <InfoLabel>Fecha</InfoLabel>
                </Grid>
                <Grid item xs={8}>
                  {isEditingDate ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      {isDemoAccount(invoice?.user_id) ? <ColombianDateField value={editedDate}
                        onChangeValue={setEditedDate} size="small" label="Fecha" disabled={isSaving} /> : <TextField
                        type="date"
                        value={editedDate}
                        onChange={(e) => setEditedDate(e.target.value)}
                        size="small"
                        disabled={isSaving}
                      />}
                      <IconButton 
                        onClick={handleDateSave} 
                        disabled={isSaving || (isDemoAccount(invoice?.user_id) && !editedDate)}
                        size="small"
                        color="primary"
                      >
                        <SaveIcon />
                      </IconButton>
                      <IconButton 
                        onClick={handleDateCancel} 
                        disabled={isSaving}
                        size="small"
                        color="secondary"
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      <InfoValue variant="body1">{formatDate(invoice.date)}</InfoValue>
                      <IconButton 
                        onClick={handleDateEdit} 
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
              </InfoGrid>
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionTitle variant="h6">{isDemoAccount(invoice?.user_id) ? 'Información del Proveedor' : 'Información del Cliente'}</SectionTitle>
              <InfoGrid container>
                <Grid item xs={4}>
                  <InfoLabel>Nombre</InfoLabel>
                </Grid>
                <Grid item xs={8}>
                  <InfoValue variant="body1">{invoice.billing_name}</InfoValue>
                </Grid>
              </InfoGrid>
              <InfoGrid container>
                <Grid item xs={4}>
                  <InfoLabel>NIT/ID</InfoLabel>
                </Grid>
                <Grid item xs={8}>
                  <InfoValue variant="body1">{invoice.num_identificacion || 'No especificado'}</InfoValue>
                </Grid>
              </InfoGrid>
              <InfoGrid container>
                <Grid item xs={4}>
                  <InfoLabel>Dirección</InfoLabel>
                </Grid>
                <Grid item xs={8}>
                  <InfoValue variant="body1">
                    {invoice.billing_street}<br />
                    {invoice.billing_city}, {invoice.billing_state || ''} {invoice.billing_zip_code || ''}<br />
                    {invoice.billing_country}
                  </InfoValue>
                </Grid>
              </InfoGrid>
            </Grid>
          </Grid>
        </InfoSection>

        {/* Service Response Section in Modal */}
        {(invoice.estado === 'SINCRONIZADO' || invoice.estado === 'ERROR') && invoice.service_response && (
          <>
            <Divider sx={{ my: 2 }} />
            <InfoSection>
              <SectionTitle variant="h6" sx={{ mb: 1 }}>Respuesta de Sincronización</SectionTitle>
              <Paper elevation={0} sx={{ p: 2, backgroundColor: invoice.estado === 'ERROR' ? alpha(theme.palette.error.light, 0.15) : alpha(theme.palette.success.light, 0.15), borderRadius: 1 }}>
                <InfoGrid container>
                  <Grid item xs={12}>
                    <InfoLabel>Mensaje del Servicio:</InfoLabel>
                    <InfoValue variant="body1" sx={{ 
                      whiteSpace: 'pre-wrap', 
                      wordBreak: 'break-word',
                      color: invoice.estado === 'ERROR' ? theme.palette.error.dark : theme.palette.text.primary // Changed success color for better readability
                    }}>
                      {invoice.service_response}
                    </InfoValue>
                  </Grid>
                </InfoGrid>
              </Paper>
            </InfoSection>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <SectionTitle variant="h6">Elementos de la Factura</SectionTitle>
        {itemMessage && <Alert severity="success" closeText="Cerrar aviso" role="status" sx={{mb:2}} onClose={() => setItemMessage('')}>{itemMessage}</Alert>}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mb: 2, width: '100%', maxHeight: 420 }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '32%', minWidth: 260 }}>Descripción</TableCell>
                {!isServiceInvoice && <TableCell sx={{ width: '22%', minWidth: 220 }}>Producto</TableCell>}
                {!isServiceInvoice && <TableCell sx={{ width: '10%', minWidth: 90 }}>Bodega</TableCell>}
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">Precio Unitario</TableCell>
                <TableCell align="right">IVA</TableCell>
                <TableCell align="right">INC</TableCell>
                <TableCell align="right">Descuento</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceItems.length > 0 ? (
                invoiceItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {item.provider_item_code ? `[${item.provider_item_code}] ` : ''}
                          {item.description}
                        </Typography>
                        {invoice.purchase_order_mapping && invoice.purchase_order_mapping.find(m => m.invoice_item_id === item.id) && (
                          <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.5, fontWeight: 'medium' }}>
                            ✓ Asignado a OC {invoice.purchase_order_mapping.find(m => m.invoice_item_id === item.id).po_number}
                          </Typography>
                        )}
                        {!isServiceInvoice && item.product_id && productById[String(item.product_id)]?.description && (
                          <Tooltip title={productById[String(item.product_id)]?.description || ''} placement="top" arrow>
                            <Box
                              component="span"
                              sx={{
                                mt: 0.5,
                                display: 'inline-flex',
                                alignSelf: 'flex-start',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.info.light, 0.18),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.info.dark,
                                  lineHeight: 1.15,
                                }}
                              >
                                {productById[String(item.product_id)]?.description}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {!isServiceInvoice && (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.product_id ? (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.info.light, 0.18),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.info.dark,
                                  lineHeight: 1.1,
                                }}
                              >
                                {productById[String(item.product_id)]?.item_code || String(item.product_id)}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="warning"
                              label={
                                userConfig?.exact_provider_item_code
                                  ? "No en Catálogo (Búsqueda estricta)"
                                  : userConfig?.po_priority_mode
                                    ? "No en OCs pendientes (Búsqueda estricta)"
                                    : "Sin producto"
                              }
                            />
                          )}
                          <IconButton size="small" onClick={() => openProductPicker(item)}>
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                    {!isServiceInvoice && (
                      <TableCell>
                        <WarehouseAutocomplete
                          initialValue={item.warehouse_code}
                          onChange={(newCode) => {
                            handleUpdateWarehouse(item.id, newCode);
                            handleBlurWarehouse(item.id, newCode);
                          }}
                          userId={userId}
                          supabase={supabase}
                        />
                      </TableCell>
                    )}
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.tax)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.impuesto_consumo)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.discount)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.total_price)}</TableCell>
                    <TableCell align="center" sx={{whiteSpace:'nowrap'}}>
                      {userConfig?.enable_invoice_item_editing === true && <Tooltip title="Editar línea">
                        <IconButton size="small" color="primary" aria-label={`Editar línea: ${item.description}`} onClick={() => openItemEditor(item)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>}
                      <IconButton size="small" color="error" aria-label={`Eliminar línea: ${item.description}`} onClick={() => confirmDelete(item.id)}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isServiceInvoice ? 8 : 9} align="center">No se encontraron elementos</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
          {enablePurchaseOrders && (
            <>
              {invoice.purchase_order_mapping && invoice.purchase_order_mapping.length > 0 ? (
                <>
                  <Button variant="outlined" color="error" onClick={async () => {
                    try {
                      await supabase.from('invoices').update({ purchase_order_mapping: null }).eq('id', invoice.id);
                      if (invoiceStore.getState().updateInvoice) await invoiceStore.getState().updateInvoice(invoice.id);
                    } catch (e) {
                      console.error(e);
                    }
                  }}>
                    Quitar Cruce
                  </Button>
                  <Button variant="outlined" color="success" onClick={() => setPoAutoFillModalOpen(true)}>
                    Ver / Modificar Cruce OC
                  </Button>
                </>
              ) : (
                <Button variant="outlined" color="secondary" onClick={() => setPoAutoFillModalOpen(true)}>
                  Autocompletar con OCs pendientes
                </Button>
              )}
            </>
          )}
          {isInventoryInvoice && enableInventoryProration && (
            <>
              {hasProratedItems && (
                <Button variant="outlined" color="warning" onClick={handleRevertProration} disabled={isProrating}>
                  Restaurar Precios Originales
                </Button>
              )}
              <Button variant="outlined" color="primary" onClick={() => setProrationModalOpen(true)} disabled={isProrating}>
                Prorratear Valores
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => openItemEditor()}>
            Añadir Línea
          </Button>
        </Box>

        <Box sx={{ maxWidth: 300, ml: 'auto' }}>
          <SummaryRow>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">{formatCurrency(displayedInvoiceTotals.subtotal)}</Typography>
          </SummaryRow>
          <SummaryRow>
            <Typography variant="body2">IVA</Typography>
            <Typography variant="body2">{formatCurrency(displayTax)}</Typography>
          </SummaryRow>
          {Boolean(totalINC > 0) && (
            <SummaryRow>
              <Typography variant="body2">Impuesto al Consumo (INC)</Typography>
              <Typography variant="body2">{formatCurrency(totalINC)}</Typography>
            </SummaryRow>
          )}
          <SummaryRow>
            <Typography variant="body2">Descuento</Typography>
            <Typography variant="body2">{formatCurrency(displayedInvoiceTotals.discount)}</Typography>
          </SummaryRow>
          <SummaryRow total={true} sx={{ mt: 1 }}>
            <Typography variant="body1">Total</Typography>
            <Typography variant="body1">{formatCurrency(displayedInvoiceTotals.total)}</Typography>
          </SummaryRow>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box display="flex" justifyContent="flex-end" gap={2} width="100%">
          {(!invoice.estado || invoice.estado === 'CAUSADA') && (
            <>
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleReject}
              >
                Desaprobar
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleApprove}
                disabled={!isInvoiceBalanced(invoice)}
              >
                Aprobar
              </Button>
            </>
          )}
          {invoice.estado === 'APROBADO' && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Las facturas aprobadas no pueden cambiar de estado
            </Typography>
          )}
          {invoice.estado === 'DESAPROBADO' && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleApprove}
              disabled={!isInvoiceBalanced(invoice)}
            >
              Aprobar
            </Button>
          )}
        </Box>
        </DialogActions>
      </Dialog>

      {!isServiceInvoice && (
        <Dialog
          open={productPickerOpen}
          onClose={closeProductPicker}
          fullWidth
          maxWidth="sm"
          PaperComponent={DraggableDialogPaper}
        >
          <DialogTitle className="draggable-dialog-title" sx={{ cursor: 'move' }}>
            Seleccionar producto
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Autocomplete
              options={productOptions}
              loading={isSearchingProducts}
              value={selectedProduct}
              onChange={(_e, next) => setSelectedProduct(next)}
              inputValue={productSearch}
              onInputChange={(_e, next) => setProductSearch(next)}
              getOptionLabel={(opt) => `${opt?.item_code || ''} - ${opt?.description || ''}`.trim()}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">
                      {option.item_code} - {option.description}
                    </Typography>
                    {option._isPODetail && (
                      <Typography variant="caption" color="info.main" display="block">
                        📋 OC {option._poTipo} {option._poNumber} | Pendiente: {option._poFaltante} | Costo: ${Number(option._poCost || 0).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar producto"
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingProducts ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={closeProductPicker}>Cancelar</Button>
              <Button
                variant="contained"
                onClick={handleSaveProductId}
                disabled={!activeItem || isSavingProduct}
              >
                {isSavingProduct ? 'Guardando...' : 'Guardar'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={prorationModalOpen} onClose={() => !isProrating && setProrationModalOpen(false)}>
        <DialogTitle>Prorratear Valores</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, mt: 1 }}>
            Ingrese el valor total adicional a distribuir proporcionalmente entre los ítems de esta factura de inventario.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Valor a Prorratear"
            type="number"
            fullWidth
            value={prorationAmount}
            onChange={(e) => setProrationAmount(e.target.value)}
            disabled={isProrating}
            sx={{ mb: 2 }}
          />
          <FreightProviderAutocomplete
            initialValue={freightProviderNit}
            onChange={(nit) => setFreightProviderNit(nit)}
            userId={userId}
            supabase={supabase}
            disabled={isProrating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProrationModalOpen(false)} disabled={isProrating}>Cancelar</Button>
          <Button onClick={handleApplyProration} variant="contained" disabled={isProrating}>
            {isProrating ? 'Aplicando...' : 'Aplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      {poAutoFillModalOpen && (
        <PurchaseOrderAutoFillModal
          open={poAutoFillModalOpen}
          onClose={() => setPoAutoFillModalOpen(false)}
          invoice={invoice}
          invoiceItems={invoiceItems}
          userId={userId}
          supabase={supabase}
          invoiceStore={invoiceStore}
          poPriorityMode={poPriorityMode}
        />
      )}
    </>
  );
};

export default InvoiceDetailModal;
